import { BlockProps } from '@/components/Block/Block';
import IconButton from '@/components/Redactie/IconButton/IconButton';
import ImagePickerModal from '@/components/Redactie/Modal/ImagePickerModal';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import { mdiCloudUpload } from '@mdi/js';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './ChatStickerBlock.module.sass';
import {
    ChatBlockProfilePicture,
    ChatBlockTypingIndicator,
} from '../ChatTextBlock/ChatTextBlock';

interface ChatStickerBlockData {
    alt_text: string;
}

const ChatStickerBlock = ({
    data,
    uuid,
    media,
    attachments,
    isEditable,
    isAnimated,
    activeChatCharacter,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const chatStickerBlockRef = useRef(null);
    const shouldReduceMotion = useReducedMotion();
    const [isSending, setIsSending] = useState<boolean>(
        !isEditable && !!isAnimated,
    );
    const [blockData, setBlockData] = useState<ChatStickerBlockData>({
        alt_text: data.alt_text ?? '',
    });
    const [showImagePickerModal, setShowImagePickerModal] = useState<boolean>(
        false,
    );
    const [imageFile, setImageFile] = useState<File | null>(attachments?.image);
    const imageFileBase64Url = useMemo(() => {
        if (!imageFile) return null;
        return URL.createObjectURL(imageFile);
    }, [imageFile, media?.image?.url]);
    const hasImage = !!imageFile || !!media?.image?.url;
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

    useEffect(() => {
        setTimeout(() => {
            if (chatStickerBlockRef.current) {
                setIsSending(false);
                onContentHeightChanged?.();
            }
        }, 2000);
        setTimeout(() => {
            if (chatStickerBlockRef.current) onFinished?.();
        }, 4000);
    }, []);

    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: imageFile
                ? {
                      image: imageFile,
                  }
                : {},
        });
    }, [blockData, imageFile]);

    return (
        <div className="relative">
            {!!activeChatCharacter && (
                <div className="absolute" aria-hidden>
                    <ChatBlockProfilePicture
                        className="absolute top-0 left-0"
                        name={activeChatCharacter.name}
                        isTyping={isSending}
                    />
                </div>
            )}
            <div
                ref={chatStickerBlockRef}
                className={`${styles['chatstickerblock']} ${
                    isEditable && styles['chatstickerblock--editable']
                } ${isAnimated && styles['chatstickerblock--animated']}`}
            >
                {isSending ? (
                    <ChatBlockTypingIndicator
                        displayName={activeChatCharacter?.displayName ?? ''}
                        isAnimated={isAnimated}
                        onClick={() => setIsSending(false)}
                    />
                ) : (
                    <div className={`${styles['chatstickerblock__bubble']}`}>
                        {isEditable ? (
                            <div className="relative">
                                <ResponsiveImage
                                    imageClassName={`${styles['chatstickerblock__bubble__image']}`}
                                    responsiveImages={
                                        imageFileBase64Url
                                            ? undefined
                                            : media?.image?.responsive_images
                                    }
                                    alt={blockData.alt_text}
                                    src={
                                        imageFileBase64Url ?? media?.image?.url
                                    }
                                />
                                <div
                                    className={
                                        hasImage
                                            ? 'absolute left-4 bottom-4'
                                            : 'm-2'
                                    }
                                >
                                    <IconButton
                                        icon={mdiCloudUpload}
                                        label="Afbeelding wijzigen"
                                        onClick={() =>
                                            setShowImagePickerModal(true)
                                        }
                                    >
                                        Afbeelding wijzigen
                                    </IconButton>
                                </div>

                                <ImagePickerModal
                                    title="Cover afbeelding"
                                    isOpen={showImagePickerModal}
                                    originalAltText={blockData.alt_text}
                                    originalImage={
                                        imageFileBase64Url ?? media?.image?.url
                                    }
                                    onClose={() =>
                                        setShowImagePickerModal(false)
                                    }
                                    imageWidth={384}
                                    imageHeight={384}
                                    imageMimeType="image/png"
                                    onImageSelected={(image, altText) => {
                                        if (image) setImageFile(image);
                                        if (altText)
                                            setBlockData({
                                                ...blockData,
                                                alt_text: altText,
                                            });
                                    }}
                                />
                            </div>
                        ) : (
                            <>
                                <motion.div
                                    onClick={() =>
                                        setIsFullScreen(!isFullScreen)
                                    }
                                    className="group relative"
                                    layoutId={
                                        shouldReduceMotion
                                            ? undefined
                                            : `chatstickerblock__bubble__image-${uuid}`
                                    }
                                    layoutDependency={showImagePickerModal}
                                >
                                    <ResponsiveImage
                                        imageClassName={`${styles['chatstickerblock__bubble__image']}`}
                                        responsiveImages={
                                            imageFileBase64Url
                                                ? undefined
                                                : media?.image
                                                      ?.responsive_images
                                        }
                                        alt={blockData.alt_text}
                                        src={
                                            imageFileBase64Url ??
                                            media?.image?.url
                                        }
                                        onLoaded={() => {
                                            onContentHeightChanged?.();
                                        }}
                                    />
                                </motion.div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatStickerBlock;
